/**
 * AppLayout - Layout principal com sidebar para páginas internas
 * Design profissional inspirado no PJE
 */

import React, { useState } from 'react';
import { LogOut, Search, RefreshCw, HelpCircle } from 'lucide-react';

const AppLayout = ({ 
  children, 
  currentUser, 
  currentPage = 'dashboard',
  onNavigate, 
  onSignOut,
  onBuscarPrazos,
  onRefresh,
  loadingSync = false
}) => {
  const [searchTerm, setSearchTerm] = useState('');

  // Títulos das páginas
  const pageTitles = {
    dashboard: 'Painel de Prazos',
    calendario: 'Calendário',
    comunicacoes: 'Comunicações',
    relatorios: 'Relatórios',
    settings: 'Configurações',
  };

  // Menu principal
  const menuItems = [
    { key: 'dashboard', label: 'Painel Principal', icon: '📊' },
    { key: 'calendario', label: 'Calendário', icon: '📅' },
    { key: 'comunicacoes', label: 'Comunicações', icon: '📧' },
    { key: 'relatorios', label: 'Relatórios', icon: '📈' },
  ];

  // Menu sistema
  const systemItems = [
    { key: 'settings', label: 'Configurações', icon: '⚙️' },
    { key: 'ajuda', label: 'Ajuda', icon: '❓' },
  ];

  // Data atual formatada
  const dataAtual = new Date().toLocaleDateString('pt-BR', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric'
  });

  return (
    <div className="min-h-screen bg-slate-100 flex">
      {/* ========== SIDEBAR FIXA - DESKTOP ========== */}
      <aside className="hidden md:flex w-64 bg-slate-900 flex-col fixed h-full z-50">
        
        {/* Logo */}
        <div className="p-4 border-b border-slate-700/50">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-emerald-500 rounded-xl flex items-center justify-center shadow-lg">
              <span className="text-xl">⚖️</span>
            </div>
            <div>
              <h1 className="text-lg font-bold text-white">JurisPrazo</h1>
              <p className="text-xs text-slate-400">Gestão de Prazos</p>
            </div>
          </div>
        </div>

        {/* Usuário */}
        <div className="p-4 border-b border-slate-700/50">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-indigo-600 rounded-full flex items-center justify-center text-white font-bold text-sm shadow-md">
              {(currentUser?.displayName || currentUser?.email || 'U').charAt(0).toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-white truncate">
                {currentUser?.displayName || 'Usuário'}
              </p>
              <p className="text-xs text-slate-400 truncate">
                {currentUser?.email || 'email@exemplo.com'}
              </p>
            </div>
            <button className="text-slate-400 hover:text-white transition-colors">
              <span className="text-lg">›</span>
            </button>
          </div>
        </div>

        {/* Campo de Busca */}
        <div className="p-4 border-b border-slate-700/50">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" size={16} />
            <input
              type="text"
              placeholder="Buscar prazo..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 bg-slate-800 border border-slate-700 rounded-lg text-sm text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
            />
          </div>
        </div>

        {/* Menu Principal */}
        <nav className="flex-1 overflow-y-auto p-4">
          {/* Seção MENU */}
          <div className="mb-6">
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3 px-3">
              Menu
            </p>
            <div className="space-y-1">
              {menuItems.map(item => (
                <button
                  key={item.key}
                  onClick={() => onNavigate && onNavigate(item.key)}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 text-sm font-medium rounded-lg transition-all ${
                    currentPage === item.key
                      ? 'text-white bg-indigo-600 shadow-lg'
                      : 'text-slate-300 hover:text-white hover:bg-slate-800'
                  }`}
                >
                  <span className="text-base">{item.icon}</span>
                  {item.label}
                </button>
              ))}
            </div>
          </div>

          {/* Seção SISTEMA */}
          <div>
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3 px-3">
              Sistema
            </p>
            <div className="space-y-1">
              {systemItems.map(item => (
                <button
                  key={item.key}
                  onClick={() => item.key !== 'ajuda' && onNavigate && onNavigate(item.key)}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 text-sm font-medium rounded-lg transition-all ${
                    currentPage === item.key
                      ? 'text-white bg-indigo-600 shadow-lg'
                      : 'text-slate-300 hover:text-white hover:bg-slate-800'
                  }`}
                >
                  <span className="text-base">{item.icon}</span>
                  {item.label}
                </button>
              ))}
            </div>
          </div>
        </nav>

        {/* Botão Sair */}
        <div className="p-4 border-t border-slate-700/50">
          <button
            onClick={onSignOut}
            className="w-full flex items-center gap-3 px-3 py-2.5 text-sm font-medium text-red-400 hover:text-red-300 hover:bg-red-500/10 rounded-lg transition-all"
          >
            <LogOut size={18} />
            Sair
          </button>
        </div>
      </aside>

      {/* ========== ÁREA DE CONTEÚDO ========== */}
      <div className="flex-1 md:ml-64 flex flex-col min-h-screen">
        
        {/* Header Superior */}
        <header className="bg-white border-b border-slate-200 px-6 py-4 sticky top-0 z-30">
          <div className="flex items-center justify-between">
            {/* Título e Data */}
            <div>
              <h1 className="text-xl font-bold text-slate-900">
                {pageTitles[currentPage] || 'Painel'}
              </h1>
              <p className="text-sm text-slate-500 capitalize">{dataAtual}</p>
            </div>

            {/* Ações */}
            <div className="flex items-center gap-3">
              {/* Botão Refresh */}
              {onRefresh && (
                <button
                  onClick={onRefresh}
                  disabled={loadingSync}
                  className="p-2 text-slate-500 hover:text-slate-700 hover:bg-slate-100 rounded-lg transition-colors disabled:opacity-50"
                  title="Atualizar"
                >
                  <RefreshCw size={20} className={loadingSync ? 'animate-spin' : ''} />
                </button>
              )}

              {/* Botão Buscar Prazos */}
              {onBuscarPrazos && (
                <button
                  onClick={onBuscarPrazos}
                  disabled={loadingSync}
                  className="flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold rounded-lg transition-colors shadow-sm disabled:opacity-50"
                >
                  <Search size={16} />
                  Buscar Prazos
                </button>
              )}
            </div>
          </div>
        </header>

        {/* Conteúdo Principal */}
        <main className="flex-1 bg-slate-100">
          {children}
        </main>
      </div>

      {/* ========== HEADER MOBILE ========== */}
      <header className="md:hidden fixed top-0 left-0 right-0 bg-slate-900 text-white z-40 shadow-lg">
        <div className="flex items-center justify-between px-4 py-3">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-emerald-500 rounded-lg flex items-center justify-center">
              <span className="text-lg">⚖️</span>
            </div>
            <span className="font-bold">JurisPrazo</span>
          </div>
          <div className="flex items-center gap-2">
            {onBuscarPrazos && (
              <button 
                onClick={onBuscarPrazos} 
                disabled={loadingSync} 
                className="p-2 bg-indigo-600 rounded-lg"
              >
                <Search size={16} />
              </button>
            )}
            <button onClick={onSignOut} className="p-2 text-red-400 hover:bg-slate-800 rounded-lg">
              <LogOut size={16} />
            </button>
          </div>
        </div>
        
        {/* Menu Mobile */}
        <nav className="flex items-center gap-1 px-4 pb-3 overflow-x-auto">
          {menuItems.map(item => (
            <button 
              key={item.key}
              onClick={() => onNavigate && onNavigate(item.key)} 
              className={`px-3 py-1.5 text-xs font-medium rounded-lg whitespace-nowrap transition-all ${
                currentPage === item.key
                  ? 'bg-indigo-600 text-white'
                  : 'text-slate-300 hover:bg-slate-800'
              }`}
            >
              {item.label}
            </button>
          ))}
        </nav>
      </header>

      {/* Espaço para header mobile */}
      <div className="md:hidden h-24"></div>
    </div>
  );
};

export default AppLayout;
